import React from "react";
import { RouteProps } from "react-router-dom";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import axios from "axios";
import Config from "config";
import { LightThemeTable } from "components/LightThemeTable";
import { fetchRequests, RequestState, createRequest, CreateRequestPayload } from "../../state/request";
import moment from "moment";
import CreateAccessRequestModal from "../../components/request-dashboard/InternalCreateRequest";
import Modal from "components/LightThemeModal";
import ExportIcon from "../../assets/img/manage_data/data_upload.svg";
import deleteTrashCan from "../../assets/img/delete-trash-can.svg";
import { RequestApprovalState, TooltipRefs } from "interfaces/requests";
import { AuthUser } from "state/auth_user";
import Constants from "helpers/constants";
import { ToastService, SeverityLevel } from "services/toastService";
import { fetchRequestStatuses, fetchRequestTypes, RequestStatus, RequestType } from "../../state/request_details_dependencies";

type RequestApprovalProps = RouteComponentProps & RouteProps & {
    style?: React.CSSProperties;
    pageName?: string;
    requests: RequestState;
    fetchRequests: (page?: number, limit?: number, status?: string, requestType?: string, requestIdSearch?: string, emailSearch?: string) => Promise<void>;
    createRequest: (payload: CreateRequestPayload) => Promise<any>;
    user?: AuthUser;
    requestStatuses: RequestStatus[];
    fetchRequestStatuses: () => void;
    requestTypes: RequestType[];
    fetchRequestTypes: () => void;
};

const styles = {
    container: { background: "#fff", padding: 10, borderRadius: 10 },
    borderedCell: {
        borderRight: "1px solid #E3E6EA"
    },
    viewDetailsLink: {
        color: "#D1A33C",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        textDecoration: "underline"
    },
    moreIndicator: {
        cursor: "pointer",
        color: "#666",
        marginLeft: 4,
        fontSize: 14,
        fontWeight: 600,
        userSelect: "none" as "none"
    },
    tooltipBase: {
        position: "fixed" as "fixed",
        backgroundColor: "#fff",
        color: "#000",
        padding: "8px 12px",
        borderRadius: 6,
        fontSize: 12,
        maxWidth: 300,
        wordWrap: "break-word" as "break-word",
        zIndex: 1000,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        border: "1px solid #E3E6EA",
        whiteSpace: "normal" as "normal"
    },
    requestedForContainer: {
        position: "relative" as "relative",
        display: "inline-flex",
        alignItems: "center"
    },
    filterContainer: {
        display: "flex",
        flexWrap: "wrap" as "wrap",
        gap: 12,
        marginBottom: 20,
        alignItems: "flex-start"
    },
    filterGroup: {
        display: "flex",
        flexDirection: "column" as "column",
        gap: 4,
        flex: "1 1 200px",
        minWidth: 160,
        maxWidth: 280
    },
    filterLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: "#333",
        marginBottom: 4
    }
};

interface ExtendedRequestApprovalState extends RequestApprovalState {
    selectedStatus: string;
    openStatusDropdown: boolean;
    emailSearch: string;
    selectedRequestType: string;
    openRequestTypeDropdown: boolean;
    requestIdSearch: string;
    exporting: boolean;
    showCancelModal: boolean;
    cancelRequestId: string | null;
    cancelRequestDisplayId: string | null;
    canceling: boolean;
}

class ManageRequests extends React.Component<RequestApprovalProps, ExtendedRequestApprovalState> {
    private tooltipRefs: TooltipRefs = {};
    private statusDropdownRef: React.RefObject<HTMLDivElement>;
    private requestTypeDropdownRef: React.RefObject<HTMLDivElement>;

    constructor(props: RequestApprovalProps) {
        super(props);
        this.state = {
            currentPage: 1,
            limit: 100,
            isModalOpen: false,
            tooltipVisible: {},
            tooltipPosition: {},
            selectedStatus: "all",
            openStatusDropdown: false,
            emailSearch: "",
            selectedRequestType: "all",
            openRequestTypeDropdown: false,
            requestIdSearch: "",
            exporting: false,
            showCancelModal: false,
            cancelRequestId: null,
            cancelRequestDisplayId: null,
            canceling: false
        };
        this.statusDropdownRef = React.createRef();
        this.requestTypeDropdownRef = React.createRef();
    }

    componentDidMount() {
        this.fetchRequestsWithFilters();
        this.props.fetchRequestStatuses();
        this.props.fetchRequestTypes();
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    fetchRequestsWithFilters = () => {
        const { currentPage, limit, selectedStatus, selectedRequestType, requestIdSearch, emailSearch } = this.state;
        this.props.fetchRequests(currentPage, limit, selectedStatus, selectedRequestType, requestIdSearch, emailSearch);
    };

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    }

    componentDidUpdate(prevProps: RequestApprovalProps) {
    }

    handleClickOutside = (event: MouseEvent) => {
        // Check if click is outside status dropdown
        if (this.statusDropdownRef.current && !this.statusDropdownRef.current.contains(event.target as Node)) {
            this.setState({ openStatusDropdown: false });
        }

        // Check if click is outside request type dropdown
        if (this.requestTypeDropdownRef.current && !this.requestTypeDropdownRef.current.contains(event.target as Node)) {
            this.setState({ openRequestTypeDropdown: false });
        }

        // Check tooltips
        let clickedOutside = true;
        Object.keys(this.tooltipRefs).forEach(key => {
            const ref = this.tooltipRefs[key];
            if (ref.current && ref.current.contains(event.target as Node)) {
                clickedOutside = false;
            }
        });

        if (clickedOutside) {
            this.setState({
                tooltipVisible: {},
                tooltipPosition: {}
            });
        }
    };

    calculateTooltipPosition = (element: HTMLElement) => {
        const rect = element.getBoundingClientRect();
        const tooltipWidth = 300;
        const tooltipHeight = 100;
        const spacing = 5;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let position: { top?: number; bottom?: number; left?: number; right?: number } = {};

        // Horizontal positioning
        // Try to position to the right first
        if (rect.right + tooltipWidth + spacing < viewportWidth) {
            position.left = rect.right + spacing;
        }
        // Try to position to the left
        else if (rect.left - tooltipWidth - spacing > 0) {
            position.right = viewportWidth - rect.left + spacing;
        }
        // Center align if neither works
        else {
            position.left = Math.max(spacing, Math.min(rect.left, viewportWidth - tooltipWidth - spacing));
        }
        // Vertical positioning
        // Try to position below first
        if (rect.bottom + tooltipHeight + spacing < viewportHeight) {
            position.top = rect.bottom + spacing;
        }
        // Position above if not enough space below
        else {
            position.bottom = viewportHeight - rect.top + spacing;
        }

        return position;
    };

    handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ selectedStatus: event.target.value });
    };

    handleEmailSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ emailSearch: event.target.value });
    };

    handleStatusFilterChange = (value: string) => {
        this.setState({ selectedStatus: value, openStatusDropdown: false, currentPage: 1 }, () => {
            this.fetchRequestsWithFilters();
        });
    };

    handleEmailSearchWithDebounce = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        this.setState({ emailSearch: value, currentPage: 1 });

        // Debounce the API call
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.fetchRequestsWithFilters();
        }, 500);
    };

    handleRequestTypeSearchWithDebounce = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // UPDATE: Add state update for selectedRequestType
        this.setState({ selectedRequestType: value, currentPage: 1 });

        // Debounce the API call
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.fetchRequestsWithFilters();
        }, 500);
    };

    handleRequestTypeFilterChange = (value: string) => {
        this.setState({ selectedRequestType: value, openRequestTypeDropdown: false, currentPage: 1 }, () => {
            this.fetchRequestsWithFilters();
        });
    };

    handleRequestIdSearchWithDebounce = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        this.setState({ requestIdSearch: value, currentPage: 1 });

        // Debounce the API call
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.fetchRequestsWithFilters();
        }, 500);
    };

    private searchTimeout: NodeJS.Timeout | null = null;

    handleCreateRequest = () => {
        this.setState({ isModalOpen: true });
    };

    handleCloseModal = () => {
        this.setState({ isModalOpen: false });
    };

    handleSubmitRequest = async () => {
        try {
            // API call to render table with latest data
            await this.fetchRequestsWithFilters();
            this.setState({ isModalOpen: false });
        } catch (error) {
            console.error("Failed to create request:", error);
        }
    };

    handleExportRequests = async () => {
        const { selectedStatus, selectedRequestType, requestIdSearch, emailSearch } = this.state;
        this.setState({ exporting: true });

        try {
            const params: any = {};
            if (selectedStatus && selectedStatus !== "all") {
                params.selectedStatus = selectedStatus;
            }
            if (selectedRequestType && selectedRequestType !== "all") {
                params.selectedRequestType = selectedRequestType;
            }
            if (requestIdSearch && requestIdSearch.trim() !== "") {
                params.requestIdSearch = requestIdSearch.trim();
            }
            if (emailSearch && emailSearch.trim() !== "") {
                params.emailSearch = emailSearch.trim();
            }

            const response = await axios.get(`${Config.API_SERVICE_URL}/api/request/export`, {
                params,
                responseType: "blob"
            });

            const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `manage-requests-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Error exporting request approvals:", error);
        } finally {
            this.setState({ exporting: false });
        }
    };

    handleViewDetails = (requestId: string) => {
        const { history } = this.props;
        history.push(`/manage-requests/requests-details/${requestId}`);
    };

    openCancelModal = (requestId: string, requestDisplayId: string) => {
        this.setState({
            showCancelModal: true,
            cancelRequestId: requestId,
            cancelRequestDisplayId: requestDisplayId
        });
    };

    handleCloseCancelModal = () => {
        this.setState({
            showCancelModal: false,
            cancelRequestId: null,
            cancelRequestDisplayId: null,
            canceling: false
        });
    };

    handleConfirmCancel = async () => {
        const { cancelRequestId } = this.state;
        if (!cancelRequestId) return;

        this.setState({ canceling: true });
        try {
            await axios.patch(`${Config.API_SERVICE_URL}/api/request/cancel/${cancelRequestId}`);
            ToastService.getInstance().publishToast(
                {
                    identifier: `request-cancel-${cancelRequestId}`,
                    title: "Request Canceled",
                    message: "The request has been canceled successfully."
                },
                SeverityLevel.SUCCESS,
                { autoClose: 3000 }
            );
            await this.fetchRequestsWithFilters();
        } catch (error: any) {
            console.error("Error canceling request:", error);
            const message = error?.response?.data?.errors?.join(", ") || error?.message || "Failed to cancel request";
            ToastService.getInstance().publishToast(
                {
                    identifier: `request-cancel-error-${cancelRequestId}`,
                    title: "Cancel Failed",
                    message
                },
                SeverityLevel.ERROR,
                { autoClose: 5000 }
            );
        } finally {
            this.handleCloseCancelModal();
        }
    };

    toggleTooltip = (rowId: string, event?: React.MouseEvent) => {
        const isCurrentlyVisible = this.state.tooltipVisible[rowId];

        if (!isCurrentlyVisible && event) {
            const element = event.currentTarget as HTMLElement;
            const position = this.calculateTooltipPosition(element);

            this.setState(prevState => ({
                tooltipVisible: {
                    ...prevState.tooltipVisible,
                    [rowId]: true
                },
                tooltipPosition: {
                    ...prevState.tooltipPosition,
                    [rowId]: position
                }
            }));
        } else {
            this.setState(prevState => ({
                tooltipVisible: {
                    ...prevState.tooltipVisible,
                    [rowId]: false
                }
            }));
        }
    };

    getStatusProps = (status: string) => {
        let pillProps = {
            text: "",
            backgroundColor: "#E0E0E0",
            color: "#666"
        };

        switch (status) {
            case "OPEN":
                pillProps = { text: "Open", backgroundColor: "#FFFACD", color: "#8B8B00" };
                break;
            case "PENDING_REVIEW":
                pillProps = { text: "Pending Review", backgroundColor: "#FFE5DC", color: "#D46A47" };
                break;
            case "UNDER_REVIEW":
                pillProps = { text: "Under Review", backgroundColor: "#FFE5DC", color: "#D46A47" };
                break;
            case "APPROVED":
                pillProps = { text: "Approved", backgroundColor: "#C2FCF2", color: "#008B7F" };
                break;
            case "REJECTED":
                pillProps = { text: "Rejected", backgroundColor: "#FFEBEE", color: "#E01F29" };
                break;
            case "WORK_IN_PROGRESS":
                pillProps = { text: "Work In Progress", backgroundColor: "#FFF8E1", color: "#F48120" };
                break;
            case "COMPLETED":
                pillProps = { text: "Completed", backgroundColor: "#C8EDD4", color: "#24A148" };
                break;
            case "CANCELLED":
                pillProps = { text: "Cancelled", backgroundColor: "#F5F5F5", color: "#757575" };
                break;
            default:
                pillProps = { text: status, backgroundColor: "#E0E0E0", color: "#666" };
        }
        return pillProps;
    };

    handlePageChange = (page: number, pageSize: number) => {
        this.setState({ currentPage: page, limit: pageSize }, () => {
            this.fetchRequestsWithFilters();
        });
    };



    getRequestedForText = (row: any) => {
        if (row.request_payload) {
            try {
                const payload = typeof row.request_payload === 'string'
                    ? JSON.parse(row.request_payload)
                    : row.request_payload;
                return Array.isArray(payload) ? payload.join(", ") : JSON.stringify(payload);
            } catch (e) {
                return row.request_payload;
            }
        }
        return "-";
    };

    renderSelect = (
        label: string,
        value: string,
        isOpen: boolean,
        onToggle: () => void,
        onSelect: (value: string) => void,
        options: Array<{ value: string, label: string }>,
        dropdownRef: React.RefObject<HTMLDivElement>
    ) => {
        // Find selected option, but don't use the "all" option for display
        const selectedOption = value !== "all" ? options.find(opt => opt.value === value) : null;
        const displayValue = selectedOption ? selectedOption.label : "";

        // Filter out the "all" option from the dropdown list
        const filteredOptions = options.filter(opt => opt.value !== "all");

        return (
            <div style={styles.filterGroup}>
                <div style={{ position: "relative", width: "100%" }} ref={dropdownRef}>
                    <div
                        style={{
                            borderRadius: 15,
                            fontSize: 12,
                            height: 32,
                            border: "1.5px solid #D1D5DB",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            padding: "0 35px 0 12px",
                            color: displayValue ? "#333" : "#999",
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.2s",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            boxSizing: "border-box" as "border-box"
                        }}
                        onClick={onToggle}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#888";
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#D1D5DB";
                        }}
                    >
                        <span style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}>{displayValue || label}</span>
                        <span
                            style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: `translateY(-50%) rotate(${isOpen ? "270deg" : "90deg"})`,
                                color: "#888",
                                fontSize: 18,
                                pointerEvents: "none",
                                transition: "transform 0.2s ease",
                                fontWeight: 600,
                            }}
                        >
                            ›
                        </span>
                    </div>

                    {isOpen && (
                        <div
                            style={{
                                position: "absolute",
                                top: 42,
                                left: 0,
                                right: 0,
                                border: "1px solid #ccc",
                                borderRadius: 6,
                                backgroundColor: "#fff",
                                zIndex: 10,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                maxHeight: 200,
                                overflowY: "auto",
                                minWidth: "100%"
                            }}
                        >
                            {/* Clear option to reset to "all" */}
                            {value !== "all" && (
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect("all");
                                    }}
                                    style={{
                                        fontSize: 12,
                                        padding: "8px 16px",
                                        cursor: "pointer",
                                        backgroundColor: "#fff",
                                        color: "#999",
                                        borderBottom: "1px solid #E3E6EA"
                                    }}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5";
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLDivElement).style.backgroundColor = "#fff";
                                    }}
                                >
                                    Clear selection
                                </div>
                            )}
                            {filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(option.value);
                                    }}
                                    style={{
                                        fontSize: 12,
                                        padding: "8px 16px",
                                        cursor: "pointer",
                                        backgroundColor: value === option.value ? "#f5f5f5" : "#fff",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word" as "break-word"
                                    }}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (value !== option.value) {
                                            (e.currentTarget as HTMLDivElement).style.backgroundColor = "#fff";
                                        }
                                    }}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    renderSearchInput = (placeholder: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (
            <div style={styles.filterGroup}>
                <input
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    style={{
                        borderRadius: 15,
                        fontSize: 12,
                        height: 32,
                        border: "1.5px solid #D1D5DB",
                        backgroundColor: "#fff",
                        padding: "0 12px",
                        color: "#333",
                        outline: "none",
                        transition: "all 0.2s",
                        fontFamily: "inherit",
                        width: "100%",
                        boxSizing: "border-box" as "border-box"
                    }}
                    onMouseEnter={(e) => {
                        (e.currentTarget as HTMLInputElement).style.borderColor = "#888";
                    }}
                    onMouseLeave={(e) => {
                        (e.currentTarget as HTMLInputElement).style.borderColor = "#D1D5DB";
                    }}
                    onFocus={(e) => {
                        (e.currentTarget as HTMLInputElement).style.borderColor = "#E5B42F";
                    }}
                    onBlur={(e) => {
                        (e.currentTarget as HTMLInputElement).style.borderColor = "#D1D5DB";
                    }}
                />
            </div>
        );
    };

    handleLimitChange = (newLimit: number) => {
        this.setState({ limit: newLimit, currentPage: 1 }, () => {
            this.fetchRequestsWithFilters();
        });
    };

    render() {
        const { style, requests } = this.props;
        const { isModalOpen, tooltipVisible, tooltipPosition, selectedStatus, openStatusDropdown, selectedRequestType, openRequestTypeDropdown } = this.state;
        const userRole = this.props.user?.role;
        const statusOptions = this.props.requestStatuses;
        const requestTypeOptions = this.props.requestTypes;
        const pageOffset = Math.max(0, (this.state.currentPage - 1) * this.state.limit);
        const pagedRows = requests?.content || [];
        const tableData = pageOffset > 0
            ? [
                ...Array.from({ length: pageOffset }, (_, idx) => ({
                    id: `pagination_row_${idx}`,
                    __placeholder: true
                })),
                ...pagedRows
            ]
            : pagedRows;

        const columns = [
            {
                field: "display_request_id",
                header: "Request ID",
                cellStyle: styles.borderedCell,
                headerStyle: styles.borderedCell
            },
            {
                field: "request_text",
                header: "Request Type",
                cellStyle: styles.borderedCell,
                headerStyle: styles.borderedCell,
                formatter: (_: any, row: any) => {
                    const requestType = row.request_text || "-";
                    const tooltipId = `tooltip-request-type-${row.id}`;

                    // If content is not available, just show "-" without "..." indicator
                    if (requestType === "-") {
                        return <span>-</span>;
                    }

                    // If text is short (30 characters or less), show complete text without "..."
                    if (requestType.length <= 30) {
                        return <span>{requestType}</span>;
                    }

                    // Create ref if it doesn't exist
                    if (!this.tooltipRefs[tooltipId]) {
                        this.tooltipRefs[tooltipId] = React.createRef<HTMLDivElement>();
                    }

                    return (
                        <div
                            style={styles.requestedForContainer}
                            ref={this.tooltipRefs[tooltipId]}
                        >
                            <span style={{ marginRight: 4 }}>
                                {requestType.substring(0, 30)}
                            </span>
                            <span
                                style={styles.moreIndicator}
                                onClick={(e) => this.toggleTooltip(tooltipId, e)}
                            >
                                ...
                            </span>
                            {tooltipVisible[tooltipId] && (
                                <div style={{
                                    ...styles.tooltipBase,
                                    ...tooltipPosition[tooltipId]
                                }}>
                                    {requestType}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                field: "request_initiator_email",
                header: "Request By",
                cellStyle: styles.borderedCell,
                headerStyle: styles.borderedCell,
                formatter: (_: any, row: any) => {
                    return row.request_initiator_email || "-";
                }
            },
            {
                field: "CreatedAt",
                header: "Request Raised On",
                cellStyle: styles.borderedCell,
                headerStyle: styles.borderedCell,
                formatter: (_: any, row: any) => {
                    return row.CreatedAt ? moment(row.CreatedAt).format("MM-DD-YYYY") : "-";
                }
            },
            {
                field: "status_code",
                header: "Request Status",
                cellStyle: styles.borderedCell,
                headerStyle: styles.borderedCell,
                formatter: (_: any, row: any) => {
                    const pillProps = this.getStatusProps(row.status_code);
                    return (
                        <div
                            style={{
                                backgroundColor: pillProps.backgroundColor,
                                color: pillProps.color,
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                display: "inline-block",
                                textAlign: "center",
                                minWidth: "100px",
                                textTransform: "capitalize",
                            }}
                        >
                            {pillProps.text}
                        </div>
                    );
                }
            },
            {
                field: "action",
                header: "Action",
                formatter: (_: any, row: any) => {
                    const canCancel =
                        row.status_code !== "CANCELLED" &&
                        row.status_code !== "COMPLETED" &&
                        (this.props.user?.role === Constants.AB_SYSTEM_ADMIN ||
                            String(row.request_initiator_user_id) === String(this.props.user?.id));

                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span
                                style={styles.viewDetailsLink}
                                onClick={() => this.handleViewDetails(row.id)}
                            >
                                View Details
                            </span>
                            {canCancel && (
                                <img
                                    src={deleteTrashCan}
                                    alt="Cancel Request"
                                    title="Cancel Request"
                                    style={{ width: 18, height: 18, cursor: "pointer" }}
                                    onClick={() => this.openCancelModal(row.id, row.display_request_id)}
                                />
                            )}
                        </div>
                    );
                }
            }
        ];

        return (
            <div style={{ ...styles.container, ...style }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16
                }}>
                    <div>
                        <h2 style={{ margin: 0, marginBottom: 5 }}>
                            Manage Requests
                        </h2>
                    </div>

                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <button
                            className="export-button"
                            onClick={this.handleExportRequests}
                            disabled={this.state.exporting}
                        >
                            <img src={ExportIcon} alt="Export Icon" className="export-icon" />
                            {this.state.exporting ? "Exporting..." : "Export Table"}
                        </button>

                        {userRole !== Constants.AB_SYSTEM_ADMIN && (
                            <button
                                className="primary-button"
                                onClick={this.handleCreateRequest}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.opacity = "0.9";
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.opacity = "1";
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '20px', lineHeight: 1, fontWeight: 200 }}>+</span>
                                    <span>Submit Request</span>
                                </div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Section */}
                <div style={styles.filterContainer}>
                    {this.renderSelect(
                        "Select Request Status",
                        selectedStatus,
                        openStatusDropdown,
                        () => this.setState({ openStatusDropdown: !openStatusDropdown }),
                        this.handleStatusFilterChange,
                        statusOptions,
                        this.statusDropdownRef
                    )}

                    {/* Request Type Select Dropdown */}
                    {this.renderSelect(
                        "Select Request Type",
                        selectedRequestType,
                        openRequestTypeDropdown,
                        () => this.setState({ openRequestTypeDropdown: !openRequestTypeDropdown }),
                        this.handleRequestTypeFilterChange,
                        requestTypeOptions,
                        this.requestTypeDropdownRef
                    )}

                    {/* Request ID Search Input */}
                    {this.renderSearchInput(
                        "Search by Request ID",
                        this.state.requestIdSearch,
                        this.handleRequestIdSearchWithDebounce
                    )}

                    {/* Email Search Input */}
                    {this.renderSearchInput(
                        "Search Requestor by Email",
                        this.state.emailSearch,
                        this.handleEmailSearchWithDebounce
                    )}
                </div>

                <LightThemeTable
                    data={tableData}
                    columns={columns}
                    isLoading={requests?.loading || false}
                    onPageChange={this.handlePageChange}
                    defaultPageSize={this.state.limit}
                    totalRecords={requests?.total || 0}
                    currentPage={this.state.currentPage}
                />

                <CreateAccessRequestModal
                    isOpen={isModalOpen}
                    onClose={this.handleCloseModal}
                    onSubmit={this.handleSubmitRequest}
                />

                <Modal
                    show={this.state.showCancelModal}
                    onClose={this.handleCloseCancelModal}
                    title="Cancel Request"
                    width="420px"
                    footer={
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                            <button
                                className="primary-button"
                                onClick={this.handleCloseCancelModal}
                                disabled={this.state.canceling}
                            >
                                Cancel
                            </button>
                            <button
                                className="primary-button"
                                onClick={this.handleConfirmCancel}
                                disabled={this.state.canceling}
                            >
                                {this.state.canceling ? "Canceling..." : "Ok"}
                            </button>
                        </div>
                    }
                >
                    <div style={{ fontSize: 14, color: "#333", lineHeight: 1.6 }}>
                        Are you sure you want to cancel request <strong>{this.state.cancelRequestDisplayId}</strong>? This action will update the request status to Cancelled.
                    </div>
                </Modal>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => {
    return {
        requests: state.requests,
        user: state.auth_user.content,
        requestStatuses: state.requestDependencies.requestStatuses,
        requestTypes: state.requestDependencies.requestTypes
    };
};

const mapDispatchToProps = {
    fetchRequests,
    createRequest,
    fetchRequestStatuses,
    fetchRequestTypes
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageRequests));